import { defineStore } from "pinia";
import { computed, ref } from "vue";
import axios from "axios";
import { useRequestsStore } from "@/stores/storeRequests";
import { useSettingsStore } from "@/stores/storeSettings";
import { API_URL } from "@/utils/constans";

export const useQuizWordsStore = defineStore("quizWords", () => {

	const storeRequests = useRequestsStore();
	const storeSettings = useSettingsStore();

	// UI состояния
	const isLoaderQuiz = ref(false);
	const isShowWelcomScreen = ref(true);
	const isShowGame = ref(false);
	const isShowEndGame = ref(false);
	const isShowResultGame = ref(false);

	// Данные игры
	const gameToken = ref(null);
	const gameProgress = ref(null);
	const dataGame = ref(null);
	const isCheckingWord = ref(false);

	// Computed
	const foundWords = computed(() => dataGame.value?.found_words || []);
	const isAllWordsFound = computed(() => foundWords.value.length === dataGame.value?.word_count);
	const isWordsGameAvailable = computed(() => storeRequests.userData?.is_leaf_fall_game_available && storeSettings.statusStockActive);
	const statusActive = computed(() => gameProgress.value?.series?.status === "active");
	const statusExpired = computed(() => gameProgress.value?.series?.status === "expired"); // "Истекла"
	const statusClosed = computed(() => gameProgress.value?.series?.status === "closed"); // "Закрыта"
	const isPausedTimer = computed(() => gameProgress.value?.series?.series_remaining_seconds === 0);

	const isShowTimeSeries = computed(() => gameProgress.value?.series?.is_timer_paused )

	// Получить прогресс игры
	const progressGame = async () => {
		try {
			const { data } = await axios.get(`${API_URL}/api/leaf-fall-game/progress/`);
			gameProgress.value = data;
			// TEMP TEST:
			// gameProgress.value = { ...gameProgress.value, series: null };
		} catch (err) {
			console.error(err);
		}
	};

	// Старт игры
	const startGame = async () => {
		isLoaderQuiz.value = true;
		isShowWelcomScreen.value = false;
		isShowGame.value = true;
		isShowEndGame.value = false;
		isShowResultGame.value = false;

		if (typeof window !== "undefined") {
			window.scrollTo({ top: 0, behavior: "smooth" });
		}

		try {
			if (!gameProgress.value) {
				await progressGame();
			}
			const levelNumber = gameProgress.value?.current_level ?? 1;
			const { data } = await axios.get(`${API_URL}/api/leaf-fall-game/level/${levelNumber}`);
			dataGame.value = data;
		} catch (err) {
			console.error(err);
		} finally {
			isLoaderQuiz.value = false;
		}
	};

	// Проверка слова через API
	const checkAndAddWord = async (word) => {
		isCheckingWord.value = true;
		try {
			const { data } = await axios.post(`${API_URL}/api/leaf-fall-game/submit-word/`, {
				level_number: dataGame.value?.level_number,
				word: word,
			});

			if (data.grid_data) {
				dataGame.value = {
					...dataGame.value,
					grid_data: data.grid_data,
					found_words: data.found_words,
					is_completed: data.is_completed,
					points_awarded: data.points_awarded,
				};
			}
			return { success: true, data };
		} catch (err) {
			const status = err.response?.status;
			return { success: false, status };
		} finally {
			isCheckingWord.value = false;
		}
	};

	// Следующий уровень
	const nextLevel = async () => {
		isLoaderQuiz.value = true;
		try {
			const nextLevelNumber = (dataGame.value?.level_number ?? 0) + 1;
			const { data } = await axios.get(`${API_URL}/api/leaf-fall-game/level/${nextLevelNumber}`);
			dataGame.value = data;
		} catch (err) {
			console.error(err);
		} finally {
			isLoaderQuiz.value = false;
		}
	};

	// Завершение игры (уровень пройден)
	const endGame = async () => {
		isShowGame.value = false;
		isShowEndGame.value = true;
	};

	// Продолжить игру (следующий уровень с экрана окончания)
	const continueGame = async () => {
		isLoaderQuiz.value = true;
		isShowGame.value = true;
		isShowEndGame.value = false;

		if (typeof window !== "undefined") {
			window.scrollTo({ top: 0, behavior: "smooth" });
		}

		try {
			await progressGame();
			const levelNumber = gameProgress.value?.current_level ?? 1;
			const { data } = await axios.get(`${API_URL}/api/leaf-fall-game/level/${levelNumber}`);
			dataGame.value = data;
		} catch (err) {
			console.error(err);
		} finally {
			isLoaderQuiz.value = false;
		}
	};

	// Полный сброс игры
	const resetGame = () => {
		dataGame.value = null;
		gameProgress.value = null;
		// gameToken.value = null;
		isShowWelcomScreen.value = true;
		isShowGame.value = false;
		isShowEndGame.value = false;
		isShowResultGame.value = false;
	};

	return {
		// UI состояния
		isLoaderQuiz,
		isShowWelcomScreen,
		isShowGame,
		isShowEndGame,
		isShowResultGame,

		// Данные
		gameToken,
		gameProgress,
		dataGame,
		isCheckingWord,

		// Computed
		foundWords,
		isAllWordsFound,
		isWordsGameAvailable,
		statusActive,
		statusExpired,
		statusClosed,
		isShowTimeSeries,
		isShowTimeSeries,

		// Методы
		progressGame,
		startGame,
		checkAndAddWord,
		nextLevel,
		endGame,
		continueGame,
		resetGame,
	};
});
